/*!
 * shapeLive.js — made shapes drawn every frame: trails, and a sound inside
 * © 2026 Rob Canning — GPLv3
 *
 *     viz(choir, trail:2)              every member leaves a tail, fading over 2 beats
 *     shape(..., fill:scope)           the shape's sound drawn inside it
 *     viz(choir, view:polar)           ... as wave, polar, phase or spectrum
 *
 * A trail belongs to what viz() named: a shape, or a cluster (one trail for
 * all its members). It is a canvas over the screen, in a foreignObject in
 * the score, so it scrolls and zooms with it. It sits beside the shape -- never inside: an
 * animation moves everything in a shape's group into its wrapper, and the
 * trail would turn with it -- or first in the cluster's group, under the
 * shapes. When the score moves under it the picture moves with it, and a
 * zoom starts it afresh. Each frame it fades (scopeViews fadeTrail, as a
 * scope's trail does), and each member's outline -- and its scope, if it
 * has one -- is painted where the member is now, through whatever turns
 * and moves it.
 *
 * fill:scope draws the sound named after the shape (the synth or audio on
 * its line, or one given the shape's uid) across it, clipped to its
 * outline, in its stroke colour. The trace is an SVG polyline beside the
 * outline in the same group, so it turns and moves with it. (One canvas
 * for all of them, under the shapes, cost the GPU twice as much: measured.)
 * No sound: a flat line.
 */

import { wavePoints, polarPoints, phasePoints, spectrumPoints, pointsAttr, fadeTrail, CANVAS_MAX, phaseLag } from "../system/scopeViews.js";

const SVG_NS = "http://www.w3.org/2000/svg";
const XHTML_NS = "http://www.w3.org/1999/xhtml";
export const SHAPE_VIEWS = new Set(["wave", "polar", "phase", "spectrum"]);

const trailed = new Set();       // shapes and clusters with a trail
const scoped = new Set();        // shapes with fill:scope
let raf = 0;

const isCluster = el => el.classList.contains("oscilla-shape-cluster");
const membersOf = owner => isCluster(owner) ? [...owner.querySelectorAll(".oscilla-shape")] : [owner];
const bodyOf = el => el.querySelector("path.oscilla-shape-body");

/** viz(sh1, trail:secs): 0 takes the trail away. */
export function setShapeTrail(owner, secs) {
  owner.dataset.vizTrail = String(secs);
  if (secs > 0) trailed.add(owner);
  else {
    trailed.delete(owner);
    owner._trail?.fo.remove();
    owner._trail = null;
  }
  wake();
}

/** fill:scope on (or another fill: off). */
export function setShapeScope(el, on) {
  if (on) {
    el.dataset.scopeFill = "1";
    scoped.add(el);
  } else if (el.dataset.scopeFill) {
    delete el.dataset.scopeFill;
    scoped.delete(el);
    el._scope?.clip.remove();
    el._scope?.line.remove();
    el._scope = null;
  }
  wake();
}

function wake() {
  if (!raf && (trailed.size || scoped.size)) raf = requestAnimationFrame(tick);
}

function tick() {
  raf = 0;
  for (const el of scoped) {
    if (!el.isConnected) { scoped.delete(el); continue; }
    drawScope(el);
  }
  for (const o of trailed) {
    if (!o.isConnected) { o._trail?.fo.remove(); trailed.delete(o); continue; }
    paintTrail(o);
  }
  wake();
}

// ---------------------------------------------------------------
//  A canvas over the screen, in the score
// ---------------------------------------------------------------

/** A canvas in a foreignObject, put in place by `place(fo)`. */
function makeLayer(place) {
  const fo = document.createElementNS(SVG_NS, "foreignObject");
  fo.style.pointerEvents = "none";
  const canvas = document.createElementNS(XHTML_NS, "canvas");
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.display = "block";
  fo.appendChild(canvas);
  place(fo);
  return { fo, canvas, x: NaN, y: NaN, per: NaN };
}

/**
 * Fit the layer to the screen, in the units of the group it is in. Moved
 * at the same scale, what is drawn moves with it; at a new scale it starts
 * afresh. Returns the matrix from screen px to canvas px, or null.
 */
function fitLayer(t) {
  const ctm = t.fo.parentNode?.getScreenCTM?.();
  if (!ctm) return null;
  const inv = ctm.inverse();
  const dpr = window.devicePixelRatio || 1;
  const per = Math.hypot(ctm.a, ctm.b);              // screen px per unit
  const W = window.innerWidth, H = window.innerHeight;
  const o = new DOMPoint(0, 0).matrixTransform(inv);
  const area = { x: o.x, y: o.y, width: W / per, height: H / per };
  const cw = Math.min(CANVAS_MAX, Math.round(W * dpr)), ch = Math.min(CANVAS_MAX, Math.round(H * dpr));
  const sx = cw / area.width, sy = ch / area.height;
  const { canvas } = t;
  if (Math.abs(t.per - per) > 1e-9 || canvas.width !== cw || canvas.height !== ch) {
    canvas.width = cw; canvas.height = ch;            // a zoom: start afresh
    t.per = per;
  } else if (Math.abs(t.x - area.x) > 1e-6 || Math.abs(t.y - area.y) > 1e-6) {
    // the score moved under it: the picture goes with the score
    const g = canvas.getContext("2d");
    g.globalCompositeOperation = "copy";
    g.drawImage(canvas, (t.x - area.x) * sx, (t.y - area.y) * sy);
    g.globalCompositeOperation = "source-over";
  }
  if (t.x !== area.x || t.y !== area.y) {
    t.fo.setAttribute("x", area.x); t.fo.setAttribute("y", area.y);
    t.fo.setAttribute("width", area.width); t.fo.setAttribute("height", area.height);
    t.x = area.x; t.y = area.y;
  }
  return new DOMMatrix([sx, 0, 0, sy, -area.x * sx, -area.y * sy]).multiply(inv);
}

/** A Path2D of the body's outline, made again only when the outline changes. */
function outline(body) {
  const d = body.getAttribute("d") || "";
  if (body._p2dFor !== d) { body._p2d = new Path2D(d); body._p2dFor = d; }
  return body._p2d;
}

/** The outline and its scope trace, in canvas px. */
function shapePaths(m, toCanvas) {
  const body = bodyOf(m);
  const bm = body?.getScreenCTM();
  if (!bm) return null;
  const mat = toCanvas.multiply(bm);
  const shape = new Path2D();
  shape.addPath(outline(body), mat);
  let trace = null;
  const pts = m._scope?.pts;
  if (pts?.length >= 4) {
    trace = new Path2D();
    trace.addPath(new Path2D(`M ${pts[0]} ${pts[1]} L ${pts.slice(2).join(" ")}`), mat);
  }
  return { body, shape, trace };
}

/** The trace, clipped to the shape (a line has no inside: not clipped). */
function strokeTrace(g, m, p, dpr) {
  if (!p.trace) return;
  g.save();
  if (Number(m.dataset.sides) !== 1) g.clip(p.shape);
  g.lineJoin = "round";
  g.strokeStyle = p.body.getAttribute("stroke") || "#eee";
  g.lineWidth = (Number(p.body.getAttribute("stroke-width")) || 2) * dpr;
  g.stroke(p.trace);
  g.restore();
}

// ---------------------------------------------------------------
//  fill:scope
// ---------------------------------------------------------------

/** The analysers of the sound called `uid`: a synth (each poly voice) or audio. */
function analysersOf(uid) {
  const out = [];
  const synths = window.activeSynthVoices;
  const add = v => { if (v?.graph?.analyser) out.push(v.graph.analyser); };
  add(synths?.get(uid));
  for (const sub of window._synthPolyGroups?.get(uid)?.subUids || []) add(synths?.get(sub));
  for (const [key, voice] of window.activeAudioCues || []) {
    if ((key === uid || key.startsWith(`${uid}__`)) && voice.analyser) out.push(voice.analyser);
  }
  return out;
}

/** This frame of the sound, every voice summed: time data, or frequency data. */
function readSound(s, analysers, freq) {
  const n = analysers[0]?.fftSize ?? 2048;
  const len = freq ? n / 2 : n;
  if (!s.sum || s.sum.length !== len || s.sumFreq !== freq) {
    s.sum = freq ? new Uint8Array(len) : new Float32Array(len);
    s.buf = freq ? new Uint8Array(len) : new Float32Array(len);
    s.sumFreq = freq;
  }
  s.sum.fill(0);
  for (const a of analysers) {
    if ((freq ? a.frequencyBinCount : a.fftSize) !== len) continue;
    if (freq) {
      a.getByteFrequencyData(s.buf);
      for (let i = 0; i < len; i++) if (s.buf[i] > s.sum[i]) s.sum[i] = s.buf[i];
    } else {
      a.getFloatTimeDomainData(s.buf);
      for (let i = 0; i < len; i++) s.sum[i] += s.buf[i];
    }
  }
  return s.sum;
}

/**
 * The view's points for a box, worked out at least 256 units across and
 * scaled back: a small shape would otherwise get a handful of samples.
 */
function pointsIn(box, fn) {
  const k = Math.max(1, 256 / Math.max(1e-6, box.width));
  const pts = fn({ x: 0, y: 0, width: box.width * k, height: box.height * k });
  for (let i = 0; i < pts.length; i += 2) {
    pts[i] = box.x + pts[i] / k;
    pts[i + 1] = box.y + pts[i + 1] / k;
  }
  return pts;
}

/** This frame's trace of the shape's sound, drawn inside it (and kept in el._scope.pts). */
function drawScope(el) {
  const body = bodyOf(el);
  if (!body) return;
  let s = el._scope;
  if (!s || !s.line.isConnected) {
    s?.clip.remove(); s?.line.remove();
    const clip = document.createElementNS(SVG_NS, "clipPath");
    clip.id = `oscilla-scope-clip-${el.id}`;
    const cp = document.createElementNS(SVG_NS, "path");
    clip.appendChild(cp);
    const line = document.createElementNS(SVG_NS, "polyline");
    line.classList.add("oscilla-shape-scope");
    line.setAttribute("fill", "none");
    line.setAttribute("vector-effect", "non-scaling-stroke");
    line.setAttribute("stroke-linejoin", "round");
    line.style.pointerEvents = "none";
    body.after(clip, line);
    s = el._scope = { clip, cp, line, pts: [] };
  }
  const ds = el.dataset;
  const line = Number(ds.sides) === 1;        // a line has no inside: the trace is the line
  const d = body.getAttribute("d");
  if (s.cp.getAttribute("d") !== d) s.cp.setAttribute("d", d);
  if (line) s.line.removeAttribute("clip-path");
  else s.line.setAttribute("clip-path", `url(#${s.clip.id})`);
  s.line.setAttribute("stroke", body.getAttribute("stroke") || "#eee");
  s.line.setAttribute("stroke-width", body.getAttribute("stroke-width") || "2");

  const cx = Number(ds.cx), cy = Number(ds.cy), rx = Number(ds.rx), ry = Number(ds.ry);
  if (![cx, cy, rx, ry].every(Number.isFinite)) return;
  const box = { x: cx - rx, y: cy - (line ? rx * 0.25 : ry), width: 2 * rx, height: line ? rx * 0.5 : 2 * ry };
  const view = SHAPE_VIEWS.has(ds.vizView) ? ds.vizView : "wave";
  const analysers = analysersOf(el.id);
  const data = readSound(s, analysers, view === "spectrum");
  const rate = analysers[0]?.context?.sampleRate ?? 48000;
  const lag = view === "phase" ? phaseLag(ds, data, s) : 12;
  s.pts = pointsIn(box, draw =>
    view === "polar" ? polarPoints(data, draw)
    : view === "phase" ? phasePoints(data, draw, lag)
    : view === "spectrum" ? spectrumPoints(data, draw, rate)
    : wavePoints(data, draw));
  s.line.setAttribute("points", pointsAttr(s.pts));
}

// ---------------------------------------------------------------
//  Trails
// ---------------------------------------------------------------

function paintTrail(owner) {
  if (!owner._trail?.fo.isConnected) {
    owner._trail = makeLayer(fo => {
      fo.classList.add("oscilla-shape-trail");
      if (isCluster(owner)) owner.prepend(fo);
      else owner.before(fo);
    });
  }
  const t = owner._trail;
  const toCanvas = fitLayer(t);
  if (!toCanvas) return;
  const g = fadeTrail(t, t.canvas, Number(owner.dataset.vizTrail) || 1);
  const dpr = window.devicePixelRatio || 1;
  for (const m of membersOf(owner)) {
    const p = shapePaths(m, toCanvas);
    if (!p) continue;
    g.globalAlpha = Number(m.getAttribute("opacity") ?? 1);
    const fill = p.body.getAttribute("fill");
    if (fill && fill !== "none") { g.fillStyle = fill; g.fill(p.shape); }
    g.lineJoin = "round";
    g.strokeStyle = p.body.getAttribute("stroke") || "#eee";
    g.lineWidth = (Number(p.body.getAttribute("stroke-width")) || 2) * dpr;
    g.stroke(p.shape);
    strokeTrace(g, m, p, dpr);
  }
  g.globalAlpha = 1;
}
